"use client";

import { c as _c } from "react/compiler-runtime";
import { cva } from "class-variance-authority";
import { Collapsible, CollapsibleContent } from "fumadocs-ui/components/ui/collapsible";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { usePathname } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { type SyntheticEvent, useEffect, useReducer, useState } from "react";
import { cn } from "../lib/cn";
import { buttonVariants } from "./ui/button";
const rateButtonVariants = cva("inline-flex items-center gap-2 px-3 py-2 rounded-full font-medium border text-sm [&_svg]:size-4 disabled:cursor-not-allowed", {
  variants: {
    active: {
      true: "bg-fd-accent text-fd-accent-foreground [&_svg]:fill-current",
      false: "text-fd-muted-foreground"
    }
  }
});
export interface Feedback {
  opinion: "good" | "bad";
  url?: string;
  message: string;
}

// localStorage throws outright in Safari Private Browsing and when site data is
// blocked, so persistence is best-effort: it must never take the widget down or
// block the confirmation UI.
function readStoredFeedback(url: string): Feedback | null {
  try {
    const item = localStorage.getItem(`docs-feedback-${url}`);
    return item === null ? null : JSON.parse(item) as Feedback;
  } catch {
    return null;
  }
}
function writeStoredFeedback(url: string, result: Feedback | null) {
  try {
    if (result) {
      localStorage.setItem(`docs-feedback-${url}`, JSON.stringify(result));
    } else {
      localStorage.removeItem(`docs-feedback-${url}`);
    }
  } catch {
    // Ignore: the rating is already recorded server-side and in PostHog.
  }
}
export function Feedback() {
  const $ = _c(36);
  const url = usePathname();
  const posthog = usePostHog();
  const [previous, replacePrevious] = useReducer(_temp, null);
  const [opinion, setOpinion] = useState(null);
  const [message, setMessage] = useState("");
  let t0;
  let t1;
  if ($[0] !== url) {
    t0 = () => {
      replacePrevious(readStoredFeedback(url));
    };
    t1 = [url];
    $[0] = url;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== message || $[4] !== opinion || $[5] !== posthog || $[6] !== url) {
    t2 = function submit(e) {
      if (opinion === null) {
        return;
      }
      const feedback = {
        opinion,
        message
      };
      posthog.capture("on_rate_docs", {
        ...feedback,
        url
      });
      replacePrevious(feedback);
      setMessage("");
      setOpinion(null);
      writeStoredFeedback(url, feedback);
      e?.preventDefault();
    };
    $[3] = message;
    $[4] = opinion;
    $[5] = posthog;
    $[6] = url;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const submit = t2;
  const activeOpinion = previous?.opinion ?? opinion;
  const t3 = opinion !== null || previous !== null;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = v => {
      if (!v) {
        setOpinion(null);
      }
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <p className="text-sm font-medium pe-2">How is this guide?</p>;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = previous !== null;
  let t7;
  if ($[10] !== activeOpinion) {
    t7 = cn(rateButtonVariants({
      active: activeOpinion === "good"
    }));
    $[10] = activeOpinion;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setOpinion("good");
    };
    t9 = <ThumbsUp />;
    $[12] = t8;
    $[13] = t9;
  } else {
    t8 = $[12];
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t6 || $[15] !== t7) {
    t10 = <button disabled={t6} className={t7} onClick={t8}>{t9}Good</button>;
    $[14] = t6;
    $[15] = t7;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = previous !== null;
  let t12;
  if ($[17] !== activeOpinion) {
    t12 = cn(rateButtonVariants({
      active: activeOpinion === "bad"
    }));
    $[17] = activeOpinion;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  let t14;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      setOpinion("bad");
    };
    t14 = <ThumbsDown />;
    $[19] = t13;
    $[20] = t14;
  } else {
    t13 = $[19];
    t14 = $[20];
  }
  let t15;
  if ($[21] !== t11 || $[22] !== t12) {
    t15 = <button disabled={t11} className={t12} onClick={t13}>{t14}Bad</button>;
    $[21] = t11;
    $[22] = t12;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== t10 || $[25] !== t15) {
    t16 = <div className="flex flex-row items-center gap-2">{t5}{t10}{t15}</div>;
    $[24] = t10;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== message || $[28] !== previous || $[29] !== submit || $[30] !== url) {
    t17 = <CollapsibleContent className="mt-3">{previous ? <div className="px-3 py-6 flex flex-col items-center gap-3 bg-fd-card text-fd-muted-foreground text-sm text-center rounded-xl"><p>Thank you for your feedback!</p><div className="flex flex-row items-center gap-2"><button className={cn(buttonVariants({
            color: "secondary"
          }), "text-xs")} onClick={() => {
            setOpinion(previous.opinion);
            replacePrevious(null);
            writeStoredFeedback(url, null);
          }}>Submit Again</button></div></div> : <form className="flex flex-col gap-3" onSubmit={submit}><textarea autoFocus={true} required={true} value={message} onChange={e_0 => {
          setMessage(e_0.target.value);
        }} className="border rounded-lg bg-fd-secondary text-fd-secondary-foreground p-3 resize-none focus-visible:outline-none placeholder:text-fd-muted-foreground" placeholder="Leave your feedback..." onKeyDown={e_1 => {
          if (!e_1.shiftKey && e_1.key === "Enter") {
            submit(e_1);
          }
        }} /><button type="submit" className={cn(buttonVariants({
          color: "outline"
        }), "w-fit px-3")}>Submit</button></form>}</CollapsibleContent>;
    $[27] = message;
    $[28] = previous;
    $[29] = submit;
    $[30] = url;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== t16 || $[33] !== t17 || $[34] !== t3) {
    t18 = <Collapsible open={t3} onOpenChange={t4} className="border-y py-3">{t16}{t17}</Collapsible>;
    $[32] = t16;
    $[33] = t17;
    $[34] = t3;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  return t18;
}
function _temp(_previous, nextPrevious) {
  return nextPrevious;
}